/* eslint-disable max-lines -- Cohesive dev-only shell: rail trigger, sheet, sidebar nav, and i18n controls. */
import i18next from 'i18next';
import { useAtom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import {
  BarChart3,
  Bell,
  CreditCard,
  Flag,
  Languages,
  type LucideIcon,
  Mic,
  PenSquare,
  Plug,
  Server,
  SquareStack,
} from 'lucide-react';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import {
  type ComponentType,
  Fragment,
  type ReactNode,
  type SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { ComplimentaryTrialEndDevSection } from '@/app-shell/complimentary-trial-end/complimentary-trial-end-dev-section';
import {
  ComplimentaryTrialEndedDevHost,
  ComplimentaryTrialEndedDevSection,
} from '@/app-shell/complimentary-trial-end/complimentary-trial-ended-dev-section';
import { RemoteDeviceSvg } from '@/assets/icons/remote-device-svg';
import { discoveryCallDevForceShowAtom } from '@/atoms/discoveryCallDevAtoms';
import {
  evidencePowerUserDevForceEnabledAtom,
  evidencePowerUserDevForceShowBannerAtom,
} from '@/atoms/evidenceTrialQueryCountAtom';
import { onboardingFlowDevForceReplayAtom } from '@/atoms/onboardingFlowDevAtoms';
import {
  sessionlessEvidenceAwarenessDevForceShowAtom,
  sessionlessEvidenceAwarenessDevVariantOverrideAtom,
} from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import { AnalyticsInspectorDevSection } from '@/components/dev/analytics-inspector-dev-section';
import { BillingDevSection } from '@/components/dev/billing-dev-section';
import { useSyncBillingDevTrialHistory } from '@/components/dev/billing-dev-trial-history';
import { ChatCapacityDevSection } from '@/components/dev/chat-capacity-dev-section';
import {
  DesktopUpdaterDevHost,
  DesktopUpdaterDevSection,
} from '@/components/dev/desktop-updater-dev-section';
import { DeviceSetupAwarenessDevSection } from '@/components/dev/device-setup-awareness-dev-section';
import { DictateOnboardingDevSection } from '@/components/dev/dictate-onboarding-dev-section';
import { DictateReengagementDevSection } from '@/components/dev/dictate-reengagement-dev-section';
import { DiscoveryCallDevSection } from '@/components/dev/discovery-call-dev-section';
import { EvidencePowerUserDevSection } from '@/components/dev/evidence-power-user-dev-section';
import { EvidenceQuotaToastDevSection } from '@/components/dev/evidence-quota-toast-dev-section';
import { FeatureFlagsDevSection } from '@/components/dev/feature-flags/feature-flags-dev-section';
import { FeatureFlagsDevSectionV2 } from '@/components/dev/feature-flags/feature-flags-dev-section-v2';
import { GamifyTrialExtensionDevSection } from '@/components/dev/gamify-trial-extension-dev-section';
import { remoteSimAppliedAtom } from '@/components/dev/heidi-remote-dev-atoms';
import { HeidiRemoteDevSection } from '@/components/dev/heidi-remote-dev-section';
import { Heidi2OnboardingDevHost } from '@/components/dev/heidi2-onboarding-dev-host';
import { Heidi2OnboardingDevSection } from '@/components/dev/heidi2-onboarding-dev-section';
import { IntegrationDevSection } from '@/components/dev/integration-dev-section';
import { LabsSessionLayoutV2DevSection } from '@/components/dev/labs-session-layout-v2-dev-section';
import { ModalPacingDevSection } from '@/components/dev/modal-pacing-dev-section';
import { ModalTierFrameworkDevSection } from '@/components/dev/modal-tier-framework-dev-section';
import { NotificationsDevSection } from '@/components/dev/notifications-dev-section';
import { OnboardingFlowDevSection } from '@/components/dev/onboarding-flow-dev-section';
import { PageEditorDevSection } from '@/components/dev/page-editor-dev-section';
import { PatientListIntroDevSection } from '@/components/dev/patient-list-intro-dev-section';
import { SeenModalsDevSection } from '@/components/dev/seen-modals-dev-section';
import { SessionlessEvidenceAwarenessDevSection } from '@/components/dev/sessionless-evidence-awareness-dev-section';
import {
  SignUpModalDevHost,
  SignUpModalDevSection,
} from '@/components/dev/sign-up-modal-dev-section';
import DisplayLanguageSelect from '@/components/ui/select/display-language-select';
import { integrationsDebuggerEnabledAtom } from '@/container/integrations/marketplace/providers/useIntegrationsDebugger';
import { useIsScribeEnableRtlEnabled } from '@/features/flags';
import { useIsFlagsSectionV2Enabled } from '@/features/flags-dev/flags-section-v2-flag';
import { flagOverridesAtom } from '@/features/flags-dev/overrides';
import { useRegion } from '@/helpers/getRegion';
import { useMutateUpdateSettings } from '@/hooks/mutation/useMutateUpdateSettings';
import {
  optimisticallySetUserLanguage,
  useQueryUserLanguage,
} from '@/hooks/useQueryUserLanguage';
import { useSelectableDisplayLanguageCodes } from '@/hooks/useSelectableDisplayLanguageCodes';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { createSafeJotaiLocalStorage } from '@/lib/jotai-storage';
import {
  analyticsInspectorEventsAtom,
  analyticsInspectorPausedAtom,
} from '@/utils/analytics/inspector/store';
import {
  getMlScribePreviewConfig,
  getTranscriptionPreviewParam,
} from '@/utils/endpoints';
import { applyPseudolocalisationToLegacyCommonJson } from '@/utils/i18n/pseudolocaliseLegacyCommonJson';
import {
  DEFAULT_LOCALE,
  getSupportedLocaleOrDefault,
  type MapLocaleToSupportedOptions,
  SupportedLocale,
} from '@/utils/i18n/supportedLocales';
import { supportedLocaleToDictationLanguage } from '@/utils/i18n/supportedLocaleToDictationLanguage';
import { logger } from '@/utils/logger/logger';
import { getEnvironment } from '@heidi/api/config';
import { billingDevOverrideAtom } from '@heidi/commerce/data';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { Evidence } from '@heidi/design-system/icons';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@heidi/design-system/sheet';
import { Switch } from '@heidi/design-system/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@heidi/design-system/tabs';
import {
  TypographyCaption,
  TypographyP2SemiBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import {
  dictateOnboardingDevLayoutOverrideAtom,
  dictateOnboardingDevPlatformOverrideAtom,
} from '@heidi/dictate';
import { showPageEditorInternalHtmlDebuggerAtom } from '@heidi/integrations/letters';
import { LanguageCode } from '@heidi/schema/ml-scribe';
import { useSeenModalsCount } from '@heidi/workspace/data';
import { FormattedMessage } from 'react-intl';

/**
 * Mirrors the supported `languageLoaders` entries in `translations.ts` so dev
 * tools can switch i18next + Next locale without importing the legacy
 * translations module.
 */
const devToolsLanguageLoaders = {
  [SupportedLocale.ArSa]: () => import('@/locales/ar-SA/common.json'),
  [SupportedLocale.CaEs]: () => import('@/locales/ca-ES/common.json'),
  [SupportedLocale.DaDk]: () => import('@/locales/da-DK/common.json'),
  [SupportedLocale.DeDe]: () => import('@/locales/de-DE/common.json'),
  [SupportedLocale.En]: () => import('@/locales/en/common.json'),
  [SupportedLocale.EnPs]: () => import('@/locales/en/common.json'),
  [SupportedLocale.EsEs]: () => import('@/locales/es-ES/common.json'),
  [SupportedLocale.FiFi]: () => import('@/locales/fi-FI/common.json'),
  [SupportedLocale.FrFr]: () => import('@/locales/fr-FR/common.json'),
  [SupportedLocale.ItIt]: () => import('@/locales/it-IT/common.json'),
  [SupportedLocale.JaJp]: () => import('@/locales/ja-JP/common.json'),
  [SupportedLocale.KoKr]: () => import('@/locales/ko-KR/common.json'),
  [SupportedLocale.NlNl]: () => import('@/locales/nl-NL/common.json'),
  [SupportedLocale.NbNo]: () => import('@/locales/nb-NO/common.json'),
  [SupportedLocale.PlPl]: () => import('@/locales/pl-PL/common.json'),
  [SupportedLocale.PtPt]: () => import('@/locales/pt-PT/common.json'),
  [SupportedLocale.SvSe]: () => import('@/locales/sv-SE/common.json'),
  [SupportedLocale.ZhCn]: () => import('@/locales/zh-CN/common.json'),
  [SupportedLocale.ZhTw]: () => import('@/locales/zh-TW/common.json'),
} as const;

async function applyRouterLocale(
  router: NextRouter,
  newLocale: string,
  mapOptions?: MapLocaleToSupportedOptions
): Promise<void> {
  const validLocale = getSupportedLocaleOrDefault(newLocale, mapOptions);
  const resources =
    await devToolsLanguageLoaders[
      validLocale as keyof typeof devToolsLanguageLoaders
    ]?.();
  if (resources) {
    let bundle: unknown = resources.default ?? resources;
    if (validLocale === SupportedLocale.EnPs) {
      bundle = await applyPseudolocalisationToLegacyCommonJson(bundle);
    }
    i18next.addResourceBundle(validLocale, 'common', bundle, true, true);
  }
  await i18next.changeLanguage(validLocale);
  void router.push(router.pathname, router.asPath, {
    locale: validLocale,
  });
}

/**
 * Small numeric badge rendered inside a TabsTrigger when one or more
 * dev-only overrides are active for that tab. Hidden entirely at zero
 * so the tab list looks clean in the default state.
 */
function TabActiveCountBadge({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  return (
    <Badge
      size="xs"
      color="green"
      className="min-w-4 justify-center px-1 tabular-nums"
      aria-label={`${count} active ${label}${count === 1 ? '' : 's'}`}
    >
      {count}
    </Badge>
  );
}

export const DEV_TOOLS_TABS = [
  'flags',
  'billing',
  'evidence',
  'dictate',
  'modals',
  'i18n',
  'editor',
  'analytics',
  'backend',
  'notifications',
  'integration',
  'remote',
] as const;
type DevToolsTab = (typeof DEV_TOOLS_TABS)[number];

const isDevToolsTab = (value: unknown): value is DevToolsTab =>
  DEV_TOOLS_TABS.includes(value as DevToolsTab);

type DevToolsNavItem = {
  value: DevToolsTab;
  label: ReactNode;
  icon: LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;
};

/**
 * Drives the desktop sidebar nav (grouped, with subheaders) and, flattened
 * below, the mobile fallback tab strip. The order and grouping here is the
 * visual order users see; `DEV_TOOLS_TABS` above is the type-list and
 * persistence contract — keep both in sync but order is owned by this list so
 * reshuffling the nav doesn't touch the persisted active tab key. Within a
 * group tabs are alphabetical (so a new tab has an obvious slot); Environment
 * is the deliberate exception, ordered Backend → Flags → Billing.
 */
const DEV_TOOLS_NAV_GROUPS: ReadonlyArray<{
  heading: string;
  items: ReadonlyArray<DevToolsNavItem>;
}> = [
  {
    heading: 'Environment',
    items: [
      { value: 'backend', label: 'Backend', icon: Server },
      { value: 'flags', label: 'Flags', icon: Flag },
      { value: 'billing', label: 'Billing', icon: CreditCard },
    ],
  },
  {
    heading: 'Product',
    items: [
      { value: 'dictate', label: 'Dictate', icon: Mic },
      { value: 'editor', label: 'Editor', icon: PenSquare },
      { value: 'evidence', label: 'Evidence', icon: Evidence },
      {
        value: 'integration',
        label: (
          <FormattedMessage
            defaultMessage="Integration"
            id="components.dev.sidebar.integrationTab"
          />
        ),
        icon: Plug,
      },
      { value: 'remote', label: 'Remote', icon: RemoteDeviceSvg },
    ],
  },
  {
    heading: 'Platform',
    items: [
      { value: 'analytics', label: 'Analytics', icon: BarChart3 },
      { value: 'i18n', label: 'i18n', icon: Languages },
      { value: 'modals', label: 'Modals', icon: SquareStack },
      { value: 'notifications', label: 'Notifications', icon: Bell },
    ],
  },
];

/** Flat nav order consumed by the mobile tab strip. */
export const DEV_TOOLS_NAV: ReadonlyArray<DevToolsNavItem> = DEV_TOOLS_NAV_GROUPS.flatMap(
  (group) => group.items
);

/**
 * Trigger classes for the desktop vertical sidebar layout. Overrides the
 * default `tabsTriggerClasses` (`inline-flex justify-center`, surface-white
 * active state): full-width ghost buttons.
 */
const SIDEBAR_TRIGGER_CLASSES = cn(
  'flex h-auto w-full shrink-0 items-center justify-start gap-2 rounded-md px-2 py-1.5 text-left text-text-secondary',
  'transition-colors hover:text-text-primary',
  'data-[state=active]:bg-background-tertiary data-[state=active]:text-text-primary data-[state=active]:shadow-none',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring-selected'
);

const devToolsActiveTabAtom = atomWithStorage<DevToolsTab>(
  'dev-tools:active-tab',
  'flags',
  createSafeJotaiLocalStorage<DevToolsTab>(),
  { getOnInit: true }
);

/**
 * Non-production developer shell: edge rail opens a slide-over sheet for ad hoc
 * tooling (i18n pseudolocale today; extend with more sections as needed).
 */
export function DevToolsSidebar() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suppressedForE2E, setSuppressedForE2E] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__IS_E2E__ === true) {
      setSuppressedForE2E(true);
    }
  }, []);
  const [activeTab, setActiveTab] = useAtom(devToolsActiveTabAtom);
  const resolvedActiveTab = isDevToolsTab(activeTab) ? activeTab : 'flags';
  const busySyncRef = useRef(false);
  const localeBeforePseudoRef = useRef<SupportedLocale | null>(null);
  const isRtlLocaleFeatureEnabled = useIsScribeEnableRtlEnabled();
  const isFlagsSectionV2Enabled = useIsFlagsSectionV2Enabled();
  const selectableDisplayLanguages = useSelectableDisplayLanguageCodes();
  const savedUserLanguage = useQueryUserLanguage();
  const { mutateAsync: updateSettings } = useMutateUpdateSettings({ showToast: false });
  const localeMapOptions = useMemo<MapLocaleToSupportedOptions>(
    () => ({ isRtlLocaleFeatureEnabled }),
    [isRtlLocaleFeatureEnabled]
  );
  const routerResolved = getSupportedLocaleOrDefault(router.locale, localeMapOptions);
  // Account language is the live UI locale. `router.locale` stays `en` on
  // desktop and the default-locale web path, so treating it as selected made
  // English impossible to re-select after switching away.
  const resolved = savedUserLanguage ?? routerResolved;
  const selectedDisplayLanguage =
    supportedLocaleToDictationLanguage(resolved) ?? LanguageCode.en;
  const isPseudo = routerResolved === SupportedLocale.EnPs;
  const environment = getEnvironment();
  const region = useRegion();
  const mlScribePreviewConfig = getMlScribePreviewConfig(region, environment);
  const transcriptionPreviewConfig = getTranscriptionPreviewParam(region, environment);
  const isMobile = useIsMobile();

  // Surface a count on the launcher trigger and inside each tab when any
  // dev-only override is active so it's obvious at a glance that the app
  // is in a non-default state. Add future toggles here as they're
  // introduced.
  const flagOverrides = useAtomValue(flagOverridesAtom);
  const billingOverride = useAtomValue(billingDevOverrideAtom);
  useSyncBillingDevTrialHistory();
  const evidenceForceEnabled = useAtomValue(evidencePowerUserDevForceEnabledAtom);
  const evidenceForceShowBanner = useAtomValue(evidencePowerUserDevForceShowBannerAtom);
  const sessionlessVariantOverride = useAtomValue(
    sessionlessEvidenceAwarenessDevVariantOverrideAtom
  );
  const sessionlessForceShow = useAtomValue(sessionlessEvidenceAwarenessDevForceShowAtom);
  const dictateOnboardingPlatformOverride = useAtomValue(
    dictateOnboardingDevPlatformOverrideAtom
  );
  const dictateOnboardingLayoutOverride = useAtomValue(
    dictateOnboardingDevLayoutOverrideAtom
  );
  const onboardingFlowForceReplay = useAtomValue(onboardingFlowDevForceReplayAtom);
  const remoteSimApplied = useAtomValue(remoteSimAppliedAtom);
  const discoveryCallForceShow = useAtomValue(discoveryCallDevForceShowAtom);
  const showPageEditorInternalHtmlDebugger = useAtomValue(
    showPageEditorInternalHtmlDebuggerAtom
  );
  const seenModalsCount = useSeenModalsCount();
  const analyticsInspectorEvents = useAtomValue(analyticsInspectorEventsAtom);
  const analyticsInspectorPaused = useAtomValue(analyticsInspectorPausedAtom);
  const integrationsDebuggerEnabled = useAtomValue(integrationsDebuggerEnabledAtom);
  const analyticsEventsCount = analyticsInspectorEvents.length;
  const flagsActiveCount = Object.keys(flagOverrides).length;
  const billingActiveCount = billingOverride != null ? 1 : 0;
  const evidenceActiveCount =
    (evidenceForceEnabled ? 1 : 0) +
    (evidenceForceShowBanner ? 1 : 0) +
    (sessionlessVariantOverride != null ? 1 : 0) +
    (sessionlessForceShow ? 1 : 0);
  // Lives under the i18n tab (market ≈ locale); its count flows through
  // i18nActiveCount into the global override total.
  const i18nActiveCount = (isPseudo ? 1 : 0) + (discoveryCallForceShow ? 1 : 0);
  const editorActiveCount = showPageEditorInternalHtmlDebugger ? 1 : 0;
  const integrationActiveCount = integrationsDebuggerEnabled ? 1 : 0;
  const dictateOnboardingActiveCount =
    (dictateOnboardingPlatformOverride != null ? 1 : 0) +
    (dictateOnboardingLayoutOverride != null ? 1 : 0);
  const onboardingFlowActiveCount = onboardingFlowForceReplay ? 1 : 0;
  const remoteActiveCount = remoteSimApplied ? 1 : 0;
  // The Seen modals tab badge counts records but does NOT contribute
  // to the rail-trigger global override count: a `seen_modals` entry
  // is real user state, not a dev override. Likewise the Analytics
  // Inspector counts observed events, not overrides — exclude from
  // the global launcher count.
  const activeOverrideCount =
    flagsActiveCount +
    billingActiveCount +
    evidenceActiveCount +
    i18nActiveCount +
    editorActiveCount +
    integrationActiveCount +
    dictateOnboardingActiveCount +
    remoteActiveCount +
    // Counted even though it lives under the Modals tab (whose badge shows
    // seen-modal records, not overrides): left on, it suppresses the
    // returning-user redirect, so the rail has to advertise it.
    onboardingFlowActiveCount;

  // Both the ml-scribe backend and the transcription service can be pointed at
  // a preview (PR) environment via URL param, and both overrides live under the
  // Backend tab. Collect whichever are active so the rail-trigger tooltip can
  // name them and the green dot / Backend tab badge light up for either.
  const activePreviews = [
    mlScribePreviewConfig ? `${mlScribePreviewConfig.value} backend` : null,
    transcriptionPreviewConfig ? `${transcriptionPreviewConfig} transcription` : null,
  ].filter((part): part is string => part != null);
  const hasActivePreview = activePreviews.length > 0;

  // Bundled into one object so both the mobile tab strip and the desktop
  // sidebar nav can share a single source of truth via `renderTabBadge`.
  const badgeCounts = {
    flagsActiveCount,
    billingActiveCount,
    evidenceActiveCount,
    seenModalsCount,
    i18nActiveCount,
    editorActiveCount,
    analyticsInspectorPaused,
    analyticsEventsCount,
    dictateOnboardingActiveCount,
    integrationActiveCount,
    remoteActiveCount,
    hasActivePreview,
  };

  const handleCloseSheet = useCallback(() => {
    setOpen(false);
  }, []);

  // Hotkey: ⌘⌥A (Mac) / Ctrl+⌥A (Win/Linux) jumps directly to the
  // Analytics Inspector. Mirrors the dual-platform pattern used in
  // `src/lists/hotkeys.ts` but stays inside the dev shell so it
  // tree-shakes out of production with the rest of this file.
  useHotkeys(
    'meta+alt+a, ctrl+alt+a',
    (event) => {
      event.preventDefault();
      setOpen(true);
      setActiveTab('analytics');
    },
    {
      enableOnFormTags: true,
      enableOnContentEditable: true,
    },
    [setActiveTab]
  );

  const setPseudoEnabled = useCallback(
    async (enabled: boolean) => {
      if (busySyncRef.current) {
        return;
      }
      if (enabled === isPseudo) {
        return;
      }

      busySyncRef.current = true;
      setBusy(true);
      try {
        if (enabled) {
          localeBeforePseudoRef.current = resolved;
          logger.debug('[dev-tools]', {
            event: 'pseudoloc_on',
            from: resolved,
            to: SupportedLocale.EnPs,
          });
          await applyRouterLocale(router, SupportedLocale.EnPs, localeMapOptions);
        } else {
          const restore = localeBeforePseudoRef.current ?? DEFAULT_LOCALE;
          logger.debug('[dev-tools]', { event: 'pseudoloc_off', restore });
          await applyRouterLocale(router, restore, localeMapOptions);
        }
      } catch (err) {
        logger.error('Dev tools: failed to toggle pseudolocale', err);
      } finally {
        busySyncRef.current = false;
        setBusy(false);
      }
    },
    [isPseudo, localeMapOptions, resolved, router]
  );

  const setDisplayLanguage = useCallback(
    async (language: LanguageCode) => {
      if (busySyncRef.current) {
        return;
      }

      const targetLocale = getSupportedLocaleOrDefault(language, localeMapOptions);
      if (targetLocale === resolved && !isPseudo) {
        return;
      }

      busySyncRef.current = true;
      setBusy(true);
      try {
        localeBeforePseudoRef.current = null;
        logger.debug('[dev-tools]', {
          event: 'display_language_select',
          from: resolved,
          language,
          to: targetLocale,
        });
        optimisticallySetUserLanguage(language);
        await updateSettings({
          language_time_settings: {
            display_language: language,
          },
        });
        await applyRouterLocale(router, language, localeMapOptions);
      } catch (err) {
        logger.error('Dev tools: failed to change display language', err);
      } finally {
        busySyncRef.current = false;
        setBusy(false);
      }
    },
    [isPseudo, localeMapOptions, resolved, router, updateSettings]
  );

  if (suppressedForE2E) return null;

  return (
    <>
      {!open ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          // z-[10000] intentionally exceeds the token scale (max z-999) so the
          // dev rail/sheet sits above toasts and modals. Dev-only.
          className="pointer-events-auto fixed right-0 top-1/2 z-[10000] -translate-y-1/2 flex-col gap-2 rounded-l-md rounded-r-none border border-r-0 border-border px-2.5 py-10 opacity-70 shadow-md transition-opacity hover:opacity-100"
          onClick={() => setOpen(true)}
          title={
            hasActivePreview
              ? `Open developer tools (${activePreviews.join(', ')} preview active)`
              : activeOverrideCount > 0
                ? `Open developer tools (${activeOverrideCount} active)`
                : 'Open developer tools'
          }
        >
          <span
            className={cn(
              typographyVariant({ variant: 'captionBold' }),
              'leading-none text-text-secondary [writing-mode:vertical-rl]'
            )}
          >
            Dev Tools
          </span>
          {hasActivePreview ? (
            <span
              data-testid="preview-indicator"
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full bg-validation-success"
            />
          ) : null}
          {activeOverrideCount > 0 ? (
            <Badge
              size="xs"
              color="green"
              className="absolute -left-1.5 -top-1.5 min-w-4 justify-center px-1 tabular-nums"
              aria-label={`${activeOverrideCount} active dev override${activeOverrideCount === 1 ? '' : 's'}`}
            >
              {activeOverrideCount}
            </Badge>
          ) : null}
        </Button>
      ) : null}

      <Sheet open={open} onOpenChange={setOpen}>
        {/* z-[10000] matches the rail trigger above. */}
        <SheetContent
          side={isMobile ? 'bottom' : 'right'}
          hideBlur={!isMobile}
          className={cn(
            'z-[10000] gap-4 overflow-hidden overscroll-contain',
            isMobile
              ? // Lock to a fixed 90vh on mobile (rather than `max-h-[90vh]`)
                // so the bottom sheet doesn't reflow shorter when the active
                // tab has less content. Inner section scrolls within.
                'h-[90vh] overflow-y-auto'
              : // Override sheet's default `sm:max-w-sm` so the desktop layout
                // has room for a left-rail sidebar nav (~160px) plus a content
                // panel without the section tabs spilling off the inline edge.
                'sm:max-w-2xl'
          )}
        >
          <SheetHeader className="text-left">
            <SheetTitle>Developer tools</SheetTitle>
            <SheetDescription>
              Local and non-production builds only. Unrelated to shipped product UI.
            </SheetDescription>
          </SheetHeader>

          <Tabs
            value={resolvedActiveTab}
            onValueChange={(next) => {
              if (isDevToolsTab(next)) {
                setActiveTab(next);
              }
            }}
            orientation={isMobile ? 'horizontal' : 'vertical'}
            className={cn(
              'flex min-h-0 flex-1',
              isMobile ? 'flex-col gap-3' : 'flex-row gap-4'
            )}
          >
            {isMobile ? (
              <div className="-mx-1 overflow-x-auto px-1">
                <TabsList
                  aria-label="Developer tools sections"
                  variant="line"
                  className="w-max min-w-full justify-start"
                >
                  {DEV_TOOLS_NAV.map((item) => (
                    <TabsTrigger
                      key={item.value}
                      value={item.value}
                      data-testid={
                        item.value === 'analytics' ? 'dev-tools-tab-analytics' : undefined
                      }
                    >
                      {item.label}
                      {renderTabBadge(item.value, badgeCounts)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            ) : (
              <TabsList
                aria-label="Developer tools sections"
                className="flex h-auto min-h-0 w-[150px] shrink-0 flex-col items-stretch justify-start gap-0.5 overflow-y-auto rounded-none bg-transparent p-0"
              >
                {DEV_TOOLS_NAV_GROUPS.map((group) => (
                  <Fragment key={group.heading}>
                    <span
                      aria-hidden="true"
                      className={cn(
                        typographyVariant({ variant: 'captionBold' }),
                        'mt-3 shrink-0 truncate px-2 pb-0.5 pt-1 text-text-primary first:mt-0'
                      )}
                    >
                      {group.heading}
                    </span>
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <TabsTrigger
                          key={item.value}
                          value={item.value}
                          data-testid={
                            item.value === 'analytics'
                              ? 'dev-tools-tab-analytics'
                              : undefined
                          }
                          className={SIDEBAR_TRIGGER_CLASSES}
                        >
                          <Icon className="size-4 shrink-0" aria-hidden="true" />
                          <span
                            className={cn(
                              typographyVariant({ variant: 'caption' }),
                              'flex-1 truncate text-start'
                            )}
                          >
                            {item.label}
                          </span>
                          {renderTabBadge(item.value, badgeCounts)}
                        </TabsTrigger>
                      );
                    })}
                  </Fragment>
                ))}
              </TabsList>
            )}

            <div
              className={cn(
                '-mr-2 mt-0 min-h-0 min-w-0 flex-1 overflow-y-auto pr-2',
                // Subtle inline-start divider:
                // separates the sidebar rail from the active section card
                // without painting a full surface change.
                !isMobile && 'border-s ps-4'
              )}
            >
              <TabsContent value="backend" className="mt-0 space-y-4">
                <section aria-labelledby="dev-tools-backend-heading">
                  <TypographyP2SemiBold id="dev-tools-backend-heading" className="mb-2">
                    Backend
                  </TypographyP2SemiBold>
                  {mlScribePreviewConfig ? (
                    <TypographyCaption className="flex items-center gap-2 text-text-secondary">
                      <span
                        data-testid="ml-scribe-preview-indicator"
                        aria-hidden="true"
                        className="size-2 rounded-full bg-validation-success"
                      />
                      Connected to preview {mlScribePreviewConfig.value} backend.
                    </TypographyCaption>
                  ) : (
                    <TypographyCaption className="text-text-tertiary">
                      Connected to default backend for {environment} environment.
                    </TypographyCaption>
                  )}
                </section>
                <section aria-labelledby="dev-tools-transcription-heading">
                  <TypographyP2SemiBold
                    id="dev-tools-transcription-heading"
                    className="mb-2"
                  >
                    Transcription Service
                  </TypographyP2SemiBold>
                  {transcriptionPreviewConfig ? (
                    <TypographyCaption className="flex items-center gap-2 text-text-secondary">
                      <span
                        data-testid="transcription-preview-indicator"
                        aria-hidden="true"
                        className="size-2 rounded-full bg-validation-success"
                      />
                      Connected to preview {transcriptionPreviewConfig} transcription
                      service.
                    </TypographyCaption>
                  ) : (
                    <TypographyCaption className="text-text-tertiary">
                      Connected to default transcription service for {environment}{' '}
                      environment.
                    </TypographyCaption>
                  )}
                </section>
              </TabsContent>

              <TabsContent value="integration" className="mt-0">
                <IntegrationDevSection />
              </TabsContent>

              <TabsContent value="flags" className="mt-0">
                {isFlagsSectionV2Enabled ? (
                  <FeatureFlagsDevSectionV2 />
                ) : (
                  <FeatureFlagsDevSection />
                )}
              </TabsContent>

              <TabsContent value="billing" className="mt-0">
                <BillingDevSection />
                <ComplimentaryTrialEndDevSection onRequestClose={handleCloseSheet} />
                <ComplimentaryTrialEndedDevSection onRequestClose={handleCloseSheet} />
                <GamifyTrialExtensionDevSection onRequestClose={handleCloseSheet} />
              </TabsContent>

              <TabsContent value="evidence" className="mt-0">
                <EvidencePowerUserDevSection onRequestClose={handleCloseSheet} />
                <SessionlessEvidenceAwarenessDevSection
                  onRequestClose={handleCloseSheet}
                />
                <EvidenceQuotaToastDevSection onRequestClose={handleCloseSheet} />
                <ChatCapacityDevSection />
              </TabsContent>

              <TabsContent value="modals" className="mt-0">
                <DesktopUpdaterDevSection onRequestClose={handleCloseSheet} />
                <LabsSessionLayoutV2DevSection onRequestClose={handleCloseSheet} />
                <PatientListIntroDevSection onRequestClose={handleCloseSheet} />
                <SignUpModalDevSection onRequestClose={handleCloseSheet} />
                <OnboardingFlowDevSection onRequestClose={handleCloseSheet} />
                <Heidi2OnboardingDevSection onRequestClose={handleCloseSheet} />
                <ModalPacingDevSection />
                <ModalTierFrameworkDevSection />
                <SeenModalsDevSection onRequestClose={handleCloseSheet} />
              </TabsContent>

              <TabsContent value="editor" className="mt-0">
                <PageEditorDevSection />
              </TabsContent>

              <TabsContent value="analytics" className="mt-0">
                <AnalyticsInspectorDevSection />
              </TabsContent>

              <TabsContent value="i18n" className="mt-0">
                <section aria-labelledby="dev-tools-i18n-heading">
                  <TypographyP2SemiBold id="dev-tools-i18n-heading" className="mb-2">
                    Internationalisation
                  </TypographyP2SemiBold>
                  <div className="mb-4 space-y-2">
                    <div className="space-y-0.5">
                      <TypographyCaption className="text-text-secondary">
                        Display language
                      </TypographyCaption>
                      <TypographyCaption className="text-text-tertiary">
                        Saves the user display language and switches app locale.
                      </TypographyCaption>
                    </div>
                    {/* z-[10001] sits one above the dev sheet (z-[10000]) so the
                        popover isn't painted behind it. Same dev-only override the
                        flag, dictate, and seen-modals tabs use. */}
                    <DisplayLanguageSelect
                      value={selectedDisplayLanguage}
                      availableLanguages={selectableDisplayLanguages}
                      contentClassName="z-[10001]"
                      disabled={busy || !router.isReady}
                      setValue={(language) => {
                        void setDisplayLanguage(language);
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <TypographyCaption className="text-text-secondary">
                        Pseudolocale ({SupportedLocale.EnPs})
                      </TypographyCaption>
                      <TypographyCaption className="text-text-tertiary">
                        Stresses react-intl ICU and legacy i18next copy; literal legacy
                        strings only.
                      </TypographyCaption>
                      <TypographyCaption className="text-text-secondary">
                        Active locale: {isPseudo ? SupportedLocale.EnPs : resolved}
                      </TypographyCaption>
                    </div>
                    <Switch
                      size="sm"
                      className="shrink-0"
                      checked={isPseudo}
                      disabled={busy || !router.isReady}
                      onCheckedChange={(on) => {
                        void setPseudoEnabled(on);
                      }}
                      aria-label="Toggle pseudolocale (en-psaccent)"
                    />
                  </div>
                </section>

                <DiscoveryCallDevSection />
              </TabsContent>

              <TabsContent value="remote" className="mt-0">
                <HeidiRemoteDevSection />
              </TabsContent>

              <TabsContent value="dictate" className="mt-0">
                <DictateOnboardingDevSection onRequestClose={handleCloseSheet} />
                <DictateReengagementDevSection onRequestClose={handleCloseSheet} />
                <DeviceSetupAwarenessDevSection onRequestClose={handleCloseSheet} />
              </TabsContent>

              <TabsContent value="notifications" className="mt-0">
                <NotificationsDevSection onRequestClose={handleCloseSheet} />
              </TabsContent>
            </div>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Mounted outside the Sheet so the force-opened sign-up modal and the
          Heidi II tour survive the sheet closing (their triggers close it). */}
      <SignUpModalDevHost />
      <Heidi2OnboardingDevHost />
      <DesktopUpdaterDevHost />
      <ComplimentaryTrialEndedDevHost />
    </>
  );
}

/**
 * Per-tab badge logic, shared between the mobile tab strip and the desktop
 * sidebar so both layouts surface the same counts. Analytics has a bespoke
 * paused-state pill; every other section uses the shared
 * `TabActiveCountBadge` so a zero count renders nothing.
 */
function renderTabBadge(
  tab: DevToolsTab,
  counts: {
    flagsActiveCount: number;
    billingActiveCount: number;
    evidenceActiveCount: number;
    seenModalsCount: number;
    i18nActiveCount: number;
    editorActiveCount: number;
    analyticsInspectorPaused: boolean;
    analyticsEventsCount: number;
    dictateOnboardingActiveCount: number;
    integrationActiveCount: number;
    remoteActiveCount: number;
    hasActivePreview: boolean;
  }
): ReactNode {
  switch (tab) {
    case 'backend':
      // Mirrors the rail-trigger dot so the at-a-glance "non-default
      // backend / transcription" affordance survives once the user opens the
      // sheet and the rail button unmounts.
      return counts.hasActivePreview ? (
        <span
          data-testid="backend-preview-indicator"
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full bg-validation-success"
        />
      ) : null;
    case 'flags':
      return <TabActiveCountBadge count={counts.flagsActiveCount} label="flag" />;
    case 'billing':
      return (
        <TabActiveCountBadge count={counts.billingActiveCount} label="billing override" />
      );
    case 'evidence':
      return (
        <TabActiveCountBadge
          count={counts.evidenceActiveCount}
          label="Evidence override"
        />
      );
    case 'modals':
      return <TabActiveCountBadge count={counts.seenModalsCount} label="seen modal" />;
    case 'i18n':
      return <TabActiveCountBadge count={counts.i18nActiveCount} label="i18n override" />;
    case 'editor':
      return (
        <TabActiveCountBadge count={counts.editorActiveCount} label="editor override" />
      );
    case 'analytics':
      return counts.analyticsInspectorPaused ? (
        <Badge
          size="xs"
          color="yellow"
          className="min-w-4 justify-center px-1"
          aria-label="Analytics inspector is paused"
        >
          ‖
        </Badge>
      ) : (
        <TabActiveCountBadge count={counts.analyticsEventsCount} label="captured event" />
      );
    case 'dictate':
      return (
        <TabActiveCountBadge
          count={counts.dictateOnboardingActiveCount}
          label="Dictate override"
        />
      );
    case 'notifications':
      return null;
    case 'integration':
      return (
        <TabActiveCountBadge
          count={counts.integrationActiveCount}
          label="integration override"
        />
      );
    case 'remote':
      return (
        <TabActiveCountBadge count={counts.remoteActiveCount} label="Remote override" />
      );
  }
}
